<template>
  <section class="v2026-am">
    <h4 class="v2026-am-title">ตั้งค่าข้อความตอบกลับอัตโนมัติ</h4>

    <SettingsTabsV2026 :tabs="amTabs"
                       :activeType="queryParams.customer_type"
                       ariaLabel="ประเภทข้อความตอบกลับอัตโนมัติ"
                       idPrefix="v2026-stab-am-"
                       panelId="v2026-am-panel"
                       @select="loadAutoMessage"/>

    <div id="v2026-am-panel"
         class="v2026-am-panel"
         role="tabpanel"
         :aria-labelledby="'v2026-stab-am-' + queryParams.customer_type"
         :aria-busy="isFetching ? 'true' : 'false'">
      <LoadingV2026 v-if="isFetching" class="v2026-am-inline-load" label="กำลังโหลด"/>

      <div v-if="loadError && !autoMessage" class="v2026-am-empty" role="status">
        <span class="v2026-am-empty__ico" aria-hidden="true"></span>
        <div class="v2026-am-empty__title">โหลดข้อความไม่สำเร็จ</div>
        <div class="v2026-am-empty__msg">{{ loadError }}</div>
        <button type="button"
                class="v2026-am-btn-ghost v2026-am-empty__act"
                @click="loadAutoMessage(queryParams.customer_type)">ลองใหม่</button>
      </div>

      <template v-else>
        <div v-if="queryParams.customer_type === 'recruitment_professional'" class="v2026-am-chatbot">
          <div class="v2026-am-chatbot__head">
            <span class="v2026-am-chatbot__ico" aria-hidden="true"></span>
            <b>AI ตอบกลับข้อความอัตโนมัติ</b>
          </div>
          <div class="v2026-am-chatbot__row">
            <span class="v2026-am-chatbot__label" id="v2026-am-chatbot-lbl">ใช้งานแชทอัตโนมัติ</span>
            <label class="v2026-am-switch">
              <input type="checkbox"
                     aria-labelledby="v2026-am-chatbot-lbl"
                     v-model="isChatbotActive"
                     @change="updateAiChatbot">
              <span class="v2026-am-switch__slider" aria-hidden="true"></span>
            </label>
          </div>
        </div>

        <div class="v2026-am-field">
          <label class="v2026-am-label" for="v2026-am-message">ข้อความตอบกลับอัตโนมัติ</label>
          <textarea id="v2026-am-message"
                    class="v2026-am-textarea"
                    rows="12"
                    v-model="formData.message"></textarea>
        </div>

        <div class="v2026-am-times">
          <div class="v2026-am-time">
            <label class="v2026-am-label">เวลาเริ่มต้น</label>
            <vue-timepicker v-model="formData.start_time"/>
          </div>
          <div class="v2026-am-time">
            <label class="v2026-am-label">เวลาสิ้นสุด</label>
            <vue-timepicker v-model="formData.end_time"/>
          </div>
        </div>

        <button type="button"
                class="v2026-am-btn-primary"
                :disabled="isLoading || !autoMessage"
                @click="updateAutoMessage">
          {{ isLoading ? 'กำลังโหลด...' : 'บันทึก' }}
        </button>
      </template>
    </div>

    <BaseToast :open="toast.open"
               :variant="toast.variant"
               :title="toast.title"
               :message="toast.message"
               :autoHideDelay="toast.variant === 'danger' ? 5000 : 2500"
               @close="toast.open = false"/>
  </section>
</template>

<script>
import axios from 'axios'
import VueTimepicker from 'vue2-timepicker'
import 'vue2-timepicker/dist/VueTimepicker.css'
import SettingsTabsV2026 from '../components/settings/SettingsTabsV2026'
import { AM_TABS } from '../constants/settingsTabs'
import BaseToast from '../components/coversation/BaseToast'
import LoadingV2026 from '../components/coversation/LoadingV2026'

export default {
  components: {
    VueTimepicker,
    SettingsTabsV2026,
    BaseToast,
    LoadingV2026
  },
  data () {
    return {
      queryParams: {
        customer_type: 'customer'
      },
      autoMessage: null,
      formData: {
        message: null,
        start_time: null,
        end_time: null
      },
      isLoading: false,
      isFetching: false,
      loadError: '',
      isChatbotActive: false,
      toast: {
        open: false,
        variant: 'success',
        title: '',
        message: ''
      }
    }
  },
  computed: {
    amTabs () {
      return AM_TABS
    }
  },
  methods: {
    errorText (err) {
      const body = err && err.response && err.response.data
      return (body && body.error) || (err && err.message) || 'เกิดข้อผิดพลาด'
    },
    showToast (variant, title, message) {
      this.toast = { open: true, variant, title, message: message || '' }
    },
    async loadAutoMessage (customerType) {
      this.queryParams.customer_type = customerType
      // professional_secondary is its own tab but the record lives under professional.
      const fetchType = customerType === 'professional_secondary' ? 'professional' : customerType
      this.isFetching = true
      this.loadError = ''
      try {
        const { data } = await axios.get('/auto-messages', { params: { customer_type: fetchType } })
        this.autoMessage = data
        this.formData.message = data.message
        this.formData.start_time = data.start_time
        this.formData.end_time = data.end_time
        if (customerType === 'recruitment_professional') {
          await this.loadChatbotAi()
        }
      } catch (err) {
        this.autoMessage = null
        this.loadError = this.errorText(err)
        this.showToast('danger', 'โหลดข้อความไม่สำเร็จ', this.loadError)
      } finally {
        this.isFetching = false
      }
    },
    async loadChatbotAi () {
      try {
        const { data } = await axios.get('/auto-messages/ai-chatbot')
        if (data) this.isChatbotActive = data.is_active
      } catch (err) {
        this.showToast('danger', 'โหลดสถานะแชทอัตโนมัติไม่สำเร็จ', this.errorText(err))
      }
    },
    async updateAutoMessage () {
      if (!this.autoMessage) return
      this.isLoading = true
      try {
        await axios.put('/auto-messages/' + this.autoMessage.id, this.formData)
        this.showToast('success', 'อัพเดทข้อมูลสำเร็จ', '')
      } catch (err) {
        this.showToast('danger', 'บันทึกไม่สำเร็จ', this.errorText(err))
      } finally {
        this.isLoading = false
      }
    },
    async updateAiChatbot () {
      try {
        await axios.put('/auto-messages/ai-chatbot', { is_active: this.isChatbotActive })
      } catch (err) {
        this.showToast('danger', 'อัพเดทแชทอัตโนมัติไม่สำเร็จ', this.errorText(err))
      }
    }
  },
  mounted () {
    this.loadAutoMessage('customer')
  }
}
</script>

<style lang="scss" scoped>
/* Only the ten --v2026-color-* tokens declared on .v2026-shell resolve under /settings, so every
   grey below is hard-coded on purpose. */
.v2026-am {
  padding: 16px 16px 48px;
  background: #ffffff;
}

.v2026-am-title {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 600;
  color: #212529;
}

.v2026-am-panel { padding-top: 16px; }

.v2026-am-inline-load {
  display: inline-flex;
  margin-bottom: 12px;
}

.v2026-am-chatbot {
  max-width: 720px;
  margin-bottom: 20px;
  padding: 16px;
  border: 1px solid #e9ecef;
  border-radius: 12px;
}

.v2026-am-chatbot__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #212529;
}

.v2026-am-chatbot__ico {
  width: 24px;
  height: 24px;
  background-color: var(--v2026-color-main-icon, #31C1D7);
  -webkit-mask: url('~@/assets/icons2026/ai-magic.svg') center / contain no-repeat;
          mask: url('~@/assets/icons2026/ai-magic.svg') center / contain no-repeat;
}

.v2026-am-chatbot__row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.v2026-am-chatbot__label {
  font-size: 14px;
  font-weight: 600;
  color: #212529;
}

.v2026-am-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  margin: 0;
  flex-shrink: 0;
}

.v2026-am-switch input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
}

.v2026-am-switch__slider {
  position: absolute;
  inset: 0;
  background: #D0D5DD;
  border-radius: 999px;
  transition: background-color 150ms ease;
}

.v2026-am-switch__slider::before {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  background: #ffffff;
  border-radius: 50%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .20);
  transition: transform 150ms ease;
}

.v2026-am-switch input:checked ~ .v2026-am-switch__slider {
  background: var(--v2026-color-main);
}

.v2026-am-switch input:checked ~ .v2026-am-switch__slider::before {
  transform: translateX(20px);
}

.v2026-am-switch input:focus-visible ~ .v2026-am-switch__slider {
  box-shadow: 0 0 0 3px rgba(49, 193, 215, .30);
}

.v2026-am-field {
  max-width: 720px;
  margin-bottom: 16px;
}

.v2026-am-label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #343a40;
}

.v2026-am-textarea {
  display: block;
  width: 100%;
  min-height: 220px;
  padding: 10px 12px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 14px;
  line-height: 1.5;
  color: #212529;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--v2026-color-main);
    box-shadow: 0 0 0 3px rgba(49, 193, 215, .30);
  }
}

.v2026-am-times {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.v2026-am-time {
  display: flex;
  flex-direction: column;
}

.v2026-am-time ::v-deep .vue__time-picker {
  font-size: 14px;
}

.v2026-am-time ::v-deep .vue__time-picker input {
  height: 36px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  font-size: 14px;
  color: #212529;
}

.v2026-am-time ::v-deep .vue__time-picker input:focus {
  outline: none;
  border-color: var(--v2026-color-main);
  box-shadow: 0 0 0 3px rgba(49, 193, 215, .30);
}

.v2026-am-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: var(--v2026-color-main);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) { filter: brightness(.94); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
  &:disabled { opacity: .5; cursor: default; }
}

.v2026-am-btn-ghost {
  height: 36px;
  padding: 0 14px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 14px;
  color: #212529;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: var(--v2026-color-hover-bg); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
}

.v2026-am-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding: 56px 16px;
}

.v2026-am-empty__ico {
  width: 40px;
  height: 40px;
  background-color: #66696C;
  opacity: .55;
  -webkit-mask: url('~@/assets/icons2026/auto-reply.svg') center / contain no-repeat;
          mask: url('~@/assets/icons2026/auto-reply.svg') center / contain no-repeat;
}

.v2026-am-empty__title {
  font-size: 15px;
  font-weight: 600;
  color: #212529;
}

.v2026-am-empty__msg {
  font-size: 13px;
  color: #66696C;
  max-width: 420px;
}

.v2026-am-empty__act { margin-top: 8px; }

@media (prefers-reduced-motion: reduce) {
  .v2026-am-switch__slider,
  .v2026-am-switch__slider::before { transition: none; }
}
</style>
